import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  cacheStatusFor,
  type CacheStatus,
  compareMany,
  type MultiCompareResult,
  type MultiCategoryStats,
  type PairStats,
} from '../utils/compare'
import { useSettings } from '../store/settings'
import { normalizeHandle } from '../utils/handle'
import { formatLocal } from '../utils/time'

function pct(n: number, total: number): string {
  if (total === 0) return '0%'
  return `${((n / total) * 100).toFixed(2)}%`
}

export function Compare() {
  const service = useSettings((s) => s.service)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [a, setA] = useState(params.get('a') ?? '')
  const [b, setB] = useState(params.get('b') ?? '')
  const [c, setC] = useState(params.get('c') ?? '')
  const [includeC, setIncludeC] = useState((params.get('c') ?? '').length > 0)
  const [result, setResult] = useState<MultiCompareResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cacheStatus, setCacheStatus] = useState<CacheStatus[] | null>(null)
  const [resolvedHandles, setResolvedHandles] = useState<string[]>([])

  async function run(rawA: string, rawB: string, rawC: string | null) {
    setLoading(true)
    setError(null)
    setResult(null)
    setCacheStatus(null)

    const handles = [rawA, rawB, ...(rawC ? [rawC] : [])]
      .map((h) => normalizeHandle(h, service))
      .filter((h) => h.length > 0)
    if (handles.length < 2) {
      setError('At least two handles required')
      setLoading(false)
      return
    }
    const dedup = new Set(handles)
    if (dedup.size !== handles.length) {
      setError('Pick distinct accounts')
      setLoading(false)
      return
    }
    setResolvedHandles(handles)

    const statuses = await Promise.all(handles.map((h) => cacheStatusFor(h)))
    setCacheStatus(statuses)
    if (statuses.some((s) => !s.hasAny)) {
      setLoading(false)
      return
    }

    try {
      const res = await compareMany(handles)
      setResult(res)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (params.get('a') && params.get('b')) {
      run(params.get('a')!, params.get('b')!, params.get('c'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const next = new URLSearchParams({ a, b })
    if (includeC && c.trim()) next.set('c', c)
    setParams(next)
    run(a, b, includeC ? c : null)
  }

  return (
    <main className="mx-auto mt-4 max-w-6xl px-4 pb-16">
      <div className="glass relative p-5">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="close compare"
          title="close compare"
          className="glass-button absolute right-3 top-3 h-8 w-8 p-0 text-base"
        >
          ✕
        </button>

        <h1 className="mb-1 pr-12 text-xl font-extrabold tracking-tight">
          Compare account action timings
        </h1>
        <p className="mb-4 text-sm text-ink-200">
          Up to three accounts. For each category I bucket every cached{' '}
          <code>createdAt</code> by ms and second.{' '}
          <span className="rounded-md border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-amber-100">
            yellow
          </span>{' '}
          = same second,{' '}
          <span className="rounded-md border border-rose-400/40 bg-rose-400/10 px-1.5 py-0.5 text-rose-100">
            red
          </span>{' '}
          = same millisecond. Three-account comparisons report every pair plus an "all three" line.
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <input
              className="glass-input min-w-0 flex-1"
              placeholder="account A handle"
              value={a}
              onChange={(e) => setA(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
            <input
              className="glass-input min-w-0 flex-1"
              placeholder="account B handle"
              value={b}
              onChange={(e) => setB(e.target.value)}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
            {includeC ? (
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <input
                  className="glass-input min-w-0 flex-1"
                  placeholder="account C handle"
                  value={c}
                  onChange={(e) => setC(e.target.value)}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
                <button
                  type="button"
                  className="glass-button text-xs"
                  onClick={() => {
                    setIncludeC(false)
                    setC('')
                  }}
                  title="remove C"
                >
                  −
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="glass-button text-xs"
                onClick={() => setIncludeC(true)}
                title="add a third account"
              >
                + add third
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="submit"
              className="glass-button glass-button--accent"
              disabled={loading || !a || !b || (includeC && !c)}
            >
              {loading ? 'comparing…' : 'compare'}
            </button>
            <span className="text-xs text-ink-400">
              tip: hit search at the top to leave compare for a profile, or ✕ to close.
            </span>
          </div>
        </form>
      </div>

      {error ? (
        <div className="glass mt-3 border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {error}
        </div>
      ) : null}

      {cacheStatus ? (
        <div className="glass mt-3 px-4 py-3 text-sm">
          <div className="mb-2 text-xs uppercase tracking-widest text-ink-300">
            cache status (records sitting in your browser)
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {cacheStatus.map((cs) => (
              <div key={cs.handle} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="font-semibold">@{cs.handle}</div>
                <div className="text-xs text-ink-300">
                  posts/replies: {cs.postCount.toLocaleString()} • likes:{' '}
                  {cs.likeCount.toLocaleString()} • reposts: {cs.repostCount.toLocaleString()}
                </div>
                {!cs.hasAny ? (
                  <div className="mt-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-2 py-1 text-xs text-amber-100">
                    Nothing cached.{' '}
                    <Link className="accent-text underline" to={`/profile/${cs.handle}`}>
                      Open the profile
                    </Link>{' '}
                    and run "Pull everything" to populate.
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="mt-4 space-y-3">
          {result.categories.map((s) => (
            <CategoryCard key={s.category} stats={s} handles={resolvedHandles} />
          ))}
          <OverallSummary result={result} />
        </div>
      ) : null}
    </main>
  )
}

function OverallSummary({ result }: { result: MultiCompareResult }) {
  const totals: number[] = result.handles.map((_, i) =>
    result.categories.reduce((s, c) => s + c.counts[i], 0),
  )
  return (
    <div className="glass-tinted p-4 text-sm">
      <div className="text-xs uppercase tracking-widest text-ink-300">overall totals</div>
      <div className="mt-1">
        {result.handles.map((h, i) => (
          <span key={h} className="mr-3 inline-block">
            <strong>@{h}</strong>: {totals[i].toLocaleString()} actions
          </span>
        ))}
      </div>
      {result.handles.length >= 3 ? (
        <p className="mt-2 text-xs text-ink-200">
          Three-account overlap totals (all three at the same time):{' '}
          {result.categories.reduce((s, c) => s + c.allOverlapSec, 0).toLocaleString()} seconds •{' '}
          {result.categories.reduce((s, c) => s + c.allOverlapMs, 0).toLocaleString()} ms
        </p>
      ) : null}
    </div>
  )
}

function CategoryCard({
  stats,
  handles,
}: {
  stats: MultiCategoryStats
  handles: string[]
}) {
  const anyOverlap =
    stats.pairs.some((p) => p.aMatchedSecond > 0 || p.bMatchedSecond > 0) ||
    stats.allOverlapSec > 0
  return (
    <details className="glass overflow-hidden">
      <summary className="cursor-pointer list-none px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-lg font-bold capitalize">{stats.category}</h2>
          {handles.map((h, i) => (
            <span key={h} className="text-xs text-ink-300">
              @{h}: <strong className="text-white">{stats.counts[i].toLocaleString()}</strong>
            </span>
          ))}
          {handles.length >= 3 ? (
            <span
              className="rounded-full border border-violet-400/40 bg-violet-400/10 px-2 py-0.5 text-xs text-violet-100"
              title="distinct timestamps where ALL handles had ≥1 action"
            >
              all 3 overlap: {stats.allOverlapSec.toLocaleString()}s · {stats.allOverlapMs.toLocaleString()}ms
            </span>
          ) : null}
          {!anyOverlap ? <span className="text-xs text-ink-400">no overlap</span> : null}
        </div>
      </summary>

      <div className="border-t border-white/5 px-4 py-3">
        {stats.pairs.map((p) => (
          <PairBlock key={`${p.iA}-${p.iB}`} stats={p} handles={handles} />
        ))}
      </div>
    </details>
  )
}

function PairBlock({ stats, handles }: { stats: PairStats; handles: string[] }) {
  const a = handles[stats.iA]
  const b = handles[stats.iB]
  return (
    <details className="mb-2 rounded-xl border border-white/10 bg-white/5">
      <summary className="cursor-pointer list-none px-3 py-2 text-xs">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-semibold">
            @{a} ↔ @{b}
          </span>
          {stats.aMatchedSecond > 0 || stats.bMatchedSecond > 0 ? (
            <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-amber-100">
              second: {pct(stats.aMatchedSecond, stats.countA)} · {pct(stats.bMatchedSecond, stats.countB)}
            </span>
          ) : null}
          {stats.aMatchedMs > 0 || stats.bMatchedMs > 0 ? (
            <span className="rounded-full border border-rose-400/40 bg-rose-400/10 px-2 py-0.5 text-rose-100">
              ms: {pct(stats.aMatchedMs, stats.countA)} · {pct(stats.bMatchedMs, stats.countB)}
            </span>
          ) : null}
          {stats.matches.length === 0 ? <span className="text-ink-400">no overlap</span> : null}
        </div>
      </summary>

      {stats.matches.length > 0 ? (
        <div className="border-t border-white/5 px-3 py-2 text-[11px]">
          <div className="mb-1 text-ink-300">
            {stats.matches.length === 500
              ? 'first 500 matches'
              : `${stats.matches.length} matches`}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-ink-400">
                  <th className="p-1 text-left">precision</th>
                  <th className="p-1 text-left">@{a} createdAt</th>
                  <th className="p-1 text-left">@{b} createdAt</th>
                  <th className="p-1 text-left">delta</th>
                </tr>
              </thead>
              <tbody>
                {stats.matches.map((m, i) => {
                  const ta = new Date(m.a.createdAt).getTime()
                  const tb = new Date(m.b.createdAt).getTime()
                  const delta = Math.abs(ta - tb)
                  return (
                    <tr
                      key={i}
                      className={m.precision === 'ms' ? 'bg-rose-500/15' : 'bg-amber-400/10'}
                    >
                      <td className="p-1 font-mono uppercase">{m.precision}</td>
                      <td className="p-1 font-mono" title={formatLocal(new Date(m.a.createdAt))}>
                        {m.a.createdAt}
                      </td>
                      <td className="p-1 font-mono" title={formatLocal(new Date(m.b.createdAt))}>
                        {m.b.createdAt}
                      </td>
                      <td className="p-1 font-mono text-ink-300">{delta}ms</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </details>
  )
}
